// O modo de operação da instância (docs/25): quais capacidades ficam acesas. A instância é sempre
// completa por baixo; o modo só decide o que aparece nos menus e o que o servidor atende. Esconder
// nunca apaga estrutura: voltar para outro modo traz tudo de volta.
import * as z from 'zod/mini'
import type { Termo } from './glossario'

export const MODOS = ['completo', 'simples', 'treinamento', 'live'] as const
/**
 * Os modos que o superadmin troca numa instância do AVA. O `live` não está aqui: o AulaStack Live é
 * produto à parte (docs/16, PO 17/10), uma instância que nasce assim e não vira AVA (nem o contrário).
 */
export const MODOS_DO_AVA = ['completo', 'simples', 'treinamento'] as const
export const Modo = z.enum(MODOS)
export type Modo = z.infer<typeof Modo>
/** O modo de quem não escolheu nada: o de sempre. Toda instância existente continua nele. */
export const MODO_PADRAO: Modo = 'completo'

export const Capacidades = z.object({
  /** vitrine, ofertas, checkout e vendas */
  venda: z.boolean(),
  /** parceiros, convênios e comissão */
  rede: z.boolean(),
  /** carteiras, planos, split e saques */
  financeiro: z.boolean(),
  /** oportunidades e Balcão */
  crm: z.boolean(),
  /** grupos, inscrição, formações atribuídas e o painel de Treinamento */
  treinamento: z.boolean(),
  /** cursos, biblioteca, alunos e a área de estudo (o AVA); desligado só no Live */
  ava: z.boolean(),
  /** o AulaStack Live: salas, transmissão, gravação e voz, com o painel Live */
  live: z.boolean(),
  /**
   * a gestão acadêmica (docs/41, docs/46): matrizes, matrícula formal, registro e documentos, no grupo Secretaria
   * da Operação. Independe do modo (uma IES também treina empresas): o superadmin liga por instância, desligada por
   * padrão, e só vale onde há AVA.
   */
  academico: z.boolean(),
  /**
   * os recursos avançados (auditoria 51-A): o que não entra no fluxo mínimo criar → vender → gerir → certificar
   * (Jornada, Pré-matrículas, Relacionamento, Avisos, Equipe dos cursos; Trilhas e Combos; os controles finos da oferta
   * e da prova). Esconder nunca apaga. Instância nova nasce com eles desligados; a existente segue como estava.
   */
  avancados: z.boolean(),
  /**
   * a pele sob medida (o quinto modo da área do aluno): ligada pelo superadmin, desligada por padrão, e só vale onde o
   * build da instância traz a skin. Aqui já vem o efetivo (ligada E instalada).
   */
  peleSobMedida: z.boolean(),
  /**
   * o painel do parceiro (docs/55): a entrada, Matricular e Meus alunos para quem opera um nó da Rede. Ligado por
   * padrão onde há rede; o superadmin desliga por instância.
   */
  painelParceiro: z.boolean(),
})
export type Capacidades = z.infer<typeof Capacidades>

const CAPACIDADES: Record<Modo, Omit<Capacidades, 'avancados' | 'peleSobMedida' | 'painelParceiro'>> = {
  completo: {
    venda: true,
    rede: true,
    financeiro: true,
    crm: true,
    treinamento: false,
    ava: true,
    live: false,
    academico: false,
  },
  // o AVA sozinho: criar o curso, ofertar (ou inscrever), operar; sem rede, split nem CRM
  simples: {
    venda: true,
    rede: false,
    financeiro: false,
    crm: false,
    treinamento: false,
    ava: true,
    live: false,
    academico: false,
  },
  treinamento: {
    venda: false,
    rede: false,
    financeiro: false,
    crm: false,
    treinamento: true,
    ava: true,
    live: false,
    academico: false,
  },
  // o AulaStack Live (produto à parte): só as salas ao vivo, a marca e a equipe
  live: {
    venda: false,
    rede: false,
    financeiro: false,
    crm: false,
    treinamento: false,
    ava: false,
    live: true,
    academico: false,
  },
}
/** `avancados` não vem do modo: é um interruptor da instância (lib/instancia.ts); aqui nasce aceso. */
/** `peleSobMedida` também não vem do modo: depende da liberação do superadmin e da skin no build (lib/instancia.ts). */
export const capacidadesDoModo = (m: Modo): Capacidades => ({
  ...CAPACIDADES[m],
  avancados: true,
  peleSobMedida: false,
  // o painel do parceiro acompanha a rede (lib/instancia.ts guarda o desligamento do superadmin)
  painelParceiro: CAPACIDADES[m].rede,
})

export const INFO_DO_MODO: Record<Modo, { rotulo: string; descricao: string }> = {
  completo: {
    rotulo: 'Completo',
    descricao: 'Tudo aceso: venda, rede, financeiro e CRM. É o modo de sempre.',
  },
  simples: {
    rotulo: 'Simples',
    descricao: 'Criar o curso, ofertar e operar. Sem rede, split, financeiro nem CRM.',
  },
  treinamento: {
    rotulo: 'Treinamento',
    descricao:
      'Para formar equipes: colaboradores em grupos, inscrição livre ou por aprovação, formações obrigatórias com prazo e o relatório de quem falta.',
  },
  live: {
    rotulo: 'AulaStack Live',
    descricao: 'Produto à parte: salas ao vivo (transmissão, reunião e voz), gravação e a marca do cliente.',
  },
}

/** O vocabulário que o modo sugere; entra no glossário ao trocar de modo (e sai se ainda for o mesmo). */
export const GLOSSARIO_DO_MODO: Partial<Record<Modo, Partial<Record<Termo, string>>>> = {
  treinamento: {
    aluno: 'colaborador',
    matricula: 'inscricao',
    instituicao: 'empresa',
    curso: 'treinamento',
    prova: 'avaliacao',
  },
  live: { aluno: 'participante' },
}

/** O modo e o que ele acende, como a instância informa a quem pergunta. */
export const ModoDaInstancia = z.object({ modo: Modo, capacidades: Capacidades })
export type ModoDaInstancia = z.infer<typeof ModoDaInstancia>
export const TrocarModo = z.object({ modo: Modo, glossario: z.optional(z.boolean()) })

/** O interruptor dos recursos avançados da instância (superadmin). */
export const ModuloAvancados = z.object({ ligado: z.boolean() })
export type ModuloAvancados = z.infer<typeof ModuloAvancados>

/** O interruptor da pele sob medida (superadmin): `ligado` é o que se guardou; `instalada`, se o build traz a skin. */
export const ModuloPeleSobMedida = z.object({ ligado: z.boolean(), instalada: z.boolean() })
export type ModuloPeleSobMedida = z.infer<typeof ModuloPeleSobMedida>
export const LigarPeleSobMedida = z.object({ ligado: z.boolean() })
